import React, { ReactElement, useState, useMemo, useEffect } from "react";
import orderBy from "lodash/orderBy";

import { DocumentFileList } from "interfaces/content";
import useLocale from "hooks/common/useLocale/useLocale";
import Document, { isDocumentFile } from "../Document/Document";
import useTranslate from "hooks/common/useTranslate/useTranslate";
import styled from "styled-components";
import styles from "styles/styles";
import TextInput from "../../common/ui/TextInput/TextInput";
import { Option } from "../../common/ui/Select/Select";
import LabeledSelect from "../../common/ui/LabeledSelect/LabeledSelect";
import { Col, Row } from "../../common/ui/FlexboxGrid";
import documentDescriptions from "../Document/DocumentDescriptions";
import Fuse from "fuse.js";

export interface Props {
    documentList: DocumentFileList;
    hideControls?: boolean;
    className?: string;
    "data-segment-id"?: string;
}

const Title = styled.h5`
    margin: 0;
    padding: 0;
    background-color: ${styles.colors.white};
`;

const DocumentRow = styled(Row)`
    margin: 0;
    background-color: ${styles.colors.white};
    padding: 0.5em 0;
`;

const DocumentContainer = styled.div`
    margin: 0;
    padding: 0;
`;

const SearchContainer = styled(Row)`
    background-color: ${styles.colors.white};
    padding: 0;
    margin: 0;
`;

const DocumentsContainer = styled.div`
    background-color: ${styles.colors.white};
    padding: 0;
    margin: 0;
`;

type SortType = undefined | "default" | "updateDate" | "alphabetical";

function DocumentList({ documentList, hideControls, className, ...props }: Props): ReactElement {
    const [sortingType, setSortingType] = useState<SortType>("default");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [documents, setDocuments] = useState(documentList.documents);

    const locale = useLocale();
    const translate = useTranslate();

    const sortedDocumentList = useMemo(() => {
        if (sortingType === "alphabetical") {
            return orderBy(documents, ["title"], ["asc"]);
        }

        if (sortingType === "updateDate") {
            return orderBy(
                documents,
                ({ publishingDate }) => publishingDate || "", // publishingDate can be undefined. To avoid affecting the order, an empty string is returned instead.
                ["desc"]
            );
        }

        if (sortingType === "default") {
            return documents;
        }

        return documents;
    }, [sortingType, documents]);

    const fuse = useMemo(() => {
        const docs = documentList.documents.map((doc) => {
            const description = documentDescriptions.find(
                (d) => isDocumentFile(doc) && d.mFileIds.includes(doc.mFilesDocument.file.id)
            );

            return {
                ...doc,
                excerpt: description?.excerpt,
                description: description?.description,
                locale: description?.locale,
            };
        });

        const fuseOptions = {
            isCaseSensitive: true,
            includeScore: true,
            threshold: 0.6,
            keys: ["title", "excerpt", "description"],
        };

        return new Fuse(docs, fuseOptions);
    }, [documentList.documents]);

    const selectOptions: Option[] = [
        {
            value: "default",
            label: translate("documentList.sortDefault"),
        },
        {
            value: "alphabetical",
            label: translate("documentList.sortByName"),
        },
        {
            value: "updateDate",
            label: translate("documentList.sortByPublishingDate"),
        },
    ];

    const onSelectSortChange = (newSortType: SortType) => {
        if (newSortType === sortingType) {
            setSortingType(undefined);

            return;
        }

        setSortingType(newSortType);
    };

    useEffect(() => {
        const results = fuse.search(searchTerm);

        const visibleDocuments = documentList.documents.filter((document) =>
            results.filter((result) => result.item.title === document.title)
        );
        setDocuments(visibleDocuments);
    }, [documentList.documents, fuse, searchTerm]);

    return (
        <div className={className} data-segment-id={props["data-segment-id"]}>
            {!hideControls && (
                <DocumentRow middle="xs" between="xs">
                    <Col xs={6}>
                        {documentList.title && <Title>{documentList.title[locale]}</Title>}
                    </Col>

                    <Col xs={6}>
                        <Row end="xs" middle="xs">
                            <LabeledSelect
                                data-testid="select"
                                options={selectOptions}
                                selectedValue={sortingType!}
                                label=""
                                onSelect={(value) => onSelectSortChange(value as SortType)}
                                unknownOption=""
                            />
                        </Row>
                    </Col>
                </DocumentRow>
            )}

            <div>
                {documentList.subTitle && <h6>{documentList.subTitle[locale]}</h6>}
                {documentList.showSearch && (
                    <SearchContainer>
                        <Col xs={12} className="searchBar">
                            <TextInput
                                placeholder={translate("search.title")}
                                value={searchTerm}
                                onChange={(value) => setSearchTerm(value)}
                            />
                        </Col>
                    </SearchContainer>
                )}
            </div>

            <DocumentsContainer>
                {sortedDocumentList.map((document, index) => (
                    <DocumentContainer key={index}>
                        <Document data-testid="document" document={document} />
                    </DocumentContainer>
                ))}
            </DocumentsContainer>
        </div>
    );
}

export default DocumentList;
